<template>
  <div class="page w-full">
    <SharedContentSection
      v-if="pageData?.content"
      v-for="(section, index) in pageData?.content"
      :key="index"
      :content="section"
      :index="index"
      :is-first="index === 0"
      :is-last="index === (pageData?.content?.length || 0) - 1"
    />
  </div>
</template>

<script setup lang="ts">
  import type { SiteOption, StrapiResponse, StrapiGraphQLResponse } from '~/types/strapi';
  import type { GraphQLPage } from '~/types/graphql';
  import type { PageEventData } from '~/types/types';

  const cacheKey = 'pageData';

  definePageMeta({
    cmsConfig: {
      strategy: 'params',
      paramKey: 'slug',
      cacheKey: cacheKey,
    },
  });

  const route = useRoute();
  const router = useRouter();
  const { locale } = useI18n();
  const runtimeConfig = useRuntimeConfig();

  // Get site options from the layout (injected)
  const siteOptions = inject<Ref<StrapiResponse<SiteOption> | null>>('siteOptions');

  const slug = computed(() => (route.params?.slug as string) || siteOptions?.value?.data?.homepage?.slug || 'homepage');

  const pageData = ref<GraphQLPage | null>(null);
  const fetchedPageData = await useGetPage(slug.value, locale.value);
  pageData.value = fetchedPageData.data.value || null;
  const fetchError = fetchedPageData.error?.value as { statusCode?: number; statusMessage?: string } | null;

  const { push } = useDataLayer();

  const trackPageViewed = () => {
    const eventData: PageEventData = {
      event: 'page_view',
      page_type: 'page',
      page_location: window.location.href,
      page_title: pageData.value?.title || '',
    };
    push(eventData);
  };

  watch([locale, slug], async () => {
    const fetchedPageData = await useGetPage(slug.value, locale.value);
    pageData.value = fetchedPageData.data.value || null;

    nextTick(() => {
      trackPageViewed();
    });
  });

  onMounted(() => {
    trackPageViewed();
  });

  // Surface upstream errors with their actual status — don't conflate "Strapi
  // returned 500" with "page doesn't exist".
  if (router.currentRoute.value.params.slug) {
    if (fetchError) {
      throw createError({
        statusCode: fetchError.statusCode || 500,
        statusMessage: fetchError.statusMessage || 'Failed to load page',
        fatal: true,
      });
    }
    if (!pageData.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found',
        fatal: true,
      });
    }
  }

  // SEO computation
  const seo = computed(() => {
    const siteUrl = runtimeConfig.public.siteURL || '';
    const currentSlug = router.currentRoute.value.params.slug;
    const fullUrl = currentSlug ? `${siteUrl}/${currentSlug}` : siteUrl;

    if (!pageData.value) {
      return {
        title: 'Homepage',
        description: 'Homepage',
        ogLocale: locale.value,
        ogImage: siteOptions?.value?.data?.seo?.metaImage?.url || '',
        ogUrl: fullUrl,
        ogType: 'website' as const,
      };
    }

    return {
      title: pageData.value.seo?.metaTitle || pageData.value.title || 'Page',
      description: pageData.value.seo?.metaDescription || 'Page',
      ogImage: pageData.value.seo?.metaImage?.url || '',
      ogUrl: fullUrl,
      ogType: 'website' as const,
      ogLocale: locale.value,
    };
  });

  // Update SEO - set route meta for layout to pick up
  watch(
    seo,
    newSeo => {
      route.meta.title = newSeo.title;
      route.meta.metaDescription = newSeo.description;
      if (pageData.value?.seo?.structuredData) {
        route.meta.structuredData = pageData.value.seo.structuredData;
      }
      route.meta.ogTitle = newSeo.title;
      route.meta.ogDescription = newSeo.description;
      route.meta.ogImage = newSeo.ogImage;
      route.meta.ogUrl = newSeo.ogUrl;
      route.meta.ogType = newSeo.ogType;
      route.meta.ogLocale = newSeo.ogLocale;
    },
    { immediate: true }
  );

  const { setTheme } = useThemeState();
  watch(pageData, newVal => {
    setTheme(newVal?.invertedHeader ? 'dark' : 'light');
  });
</script>
